<template>
  <a-modal
    :visible="props.visible"
    :title="$t('offers.create_new_attribute_value')"
    :width="700"
    centered
    :okText="$t('common.add')"
    :cancelText="$t('common.cancel')"
    :confirmLoading="isFetching"
    :closable="!isBusy"
    :maskClosable="!isBusy"
    :keyboard="!isBusy"
    :cancelButtonProps="{ props: { disabled: isBusy } }"
    :okButtonProps="{ props: { disabled: !!translatingLocale } }"
    @cancel="onClose"
    @ok="onSubmit"
  >
    <a-form-model
      ref="formRef"
      :model="form"
      :rules="formRules"
      :colon="false"
      layout="vertical"
      class="attribute-value-form"
    >
      <template v-if="props.attributeValueType === 'STRING'">
        <a-form-model-item
          label="UA"
          prop="value_mlang.uk"
          v-bind="getFieldErrorProps('value_mlang.uk')"
        >
          <div class="attribute-value-form__row">
            <a-input
              v-model.trim="form.value_mlang.uk"
              @input="clearServerError('value_mlang.uk')"
              :disabled="isBusy"
            />
            <a-button
              :disabled="isBusy || !form.value_mlang.uk"
              :loading="translatingLocale === 'uk'"
              @click="translateValue('uk')"
            >
              <a-icon
                type="swap"
                :rotate="90"
              />
              {{ $t("offers.translate") }}
            </a-button>
          </div>
        </a-form-model-item>
        <a-form-model-item
          label="RU"
          prop="value_mlang.ru"
          v-bind="getFieldErrorProps('value_mlang.ru')"
        >
          <div class="attribute-value-form__row">
            <a-input
              v-model.trim="form.value_mlang.ru"
              @input="clearServerError('value_mlang.ru')"
              :disabled="isBusy"
            />
            <a-button
              :disabled="isBusy || !form.value_mlang.ru"
              :loading="translatingLocale === 'ru'"
              @click="translateValue('ru')"
            >
              <a-icon
                type="swap"
                :rotate="90"
              />
              {{ $t("offers.translate") }}
            </a-button>
          </div>
        </a-form-model-item>
      </template>
      <template v-else>
        <a-form-model-item
          :label="$t('offers.create_form_placeholders.input_numeric_value')"
          prop="value"
          v-bind="getFieldErrorProps('value')"
        >
          <a-input-number
            v-model="form.value"
            @change="clearServerError('value')"
            :disabled="isBusy"
            style="width: 100%"
          />
        </a-form-model-item>
        <a-form-model-item
          :label="$t('offers.create_form_placeholders.choose_unit')"
          prop="unit"
          v-bind="getFieldErrorProps('unit')"
        >
          <InfiniteScrollSelect
            :value="form.unit"
            :isDisabled="isBusy"
            :placeholder="`${$t('offers.create_form_placeholders.start_typing_to_search')}`"
            :customRequest="fetchAttributeUnits"
            :customRequestParams="{ code: props.attributeCode }"
            labelKey="name"
            valueKey="oid"
            allowClear
            :showSearch="false"
            onFocusFetch
            @change="handleUnitChange"
          />
        </a-form-model-item>
      </template>
    </a-form-model>
  </a-modal>
</template>

<script setup lang="ts">
import type { FormModel } from "ant-design-vue"
import { message } from "ant-design-vue"
import { computed, reactive, ref, watch } from "vue"

import InfiniteScrollSelect from "@/components/Common/InfiniteScrollSelect/index.vue"
import useForm from "@/composables/useForm"
import { useServerFormErrors } from "@/composables/useServerFormErrors"
import i18n from "@/i18n"
import { fetchAttributeUnits, translateText } from "@/modules/offers/api/productModeration"
import type {
  AttributeFormLocale,
  CreateAttributeValueFormValues
} from "@/modules/offers/types/createOffer"
import type {
  AttributeValueType,
  ProductModerationAttributeValue
} from "@/modules/offers/types/offers"

import { findOrCreateAttributeValue } from "../../utils/findOrCreateAttributeValue"

type Props = {
  visible?: boolean
  attributeCode?: string
  attributeValueType?: AttributeValueType
}
type Emits = {
  (e: "close"): void
  (e: "submit", data: ProductModerationAttributeValue): void
}

const props: Props = defineProps<Props>()
const emit = defineEmits<Emits>()

const formRef = ref<FormModel>()
const form = reactive<CreateAttributeValueFormValues>({
  value_mlang: { uk: "", ru: "" },
  value: undefined,
  unit: undefined
})

const { validateForm, isValidForm, resetFields } = useForm(formRef)
const { getFieldErrorProps, setServerErrors, clearServerError, clearServerErrors } =
  useServerFormErrors()

const formRules = computed(() => {
  const required = [
    {
      required: true,
      whitespace: true,
      message: `${i18n.t("offers.product_moderation_errors.required")}`
    }
  ]

  if (props.attributeValueType === "STRING") {
    return { "value_mlang.uk": required, "value_mlang.ru": required }
  }

  return {
    value: [
      {
        required: true,
        type: "number",
        message: `${i18n.t("offers.product_moderation_errors.required")}`
      }
    ]
  }
})

const onClose = () => {
  emit("close")
}
const handleUnitChange = (value?: string) => {
  form.unit = value
  clearServerError("unit")
}

const isFetching = ref<boolean>(false)
const translatingLocale = ref<AttributeFormLocale>()

const isBusy = computed(() => isFetching.value || !!translatingLocale.value)

const translateValue = async (locale: AttributeFormLocale) => {
  if (isBusy.value) return

  const text = form.value_mlang[locale]

  if (!text) return

  const targetLocale = locale === "uk" ? "ru" : "uk"
  translatingLocale.value = locale
  try {
    const { data } = await translateText({ text, target_locale: targetLocale })
    form.value_mlang[targetLocale] = data.text.trim()
    clearServerError(`value_mlang.${targetLocale}`)
    formRef.value.clearValidate(`value_mlang.${targetLocale}`)
  } catch (error) {
    setServerErrors(error, { text: [`value_mlang.${locale}`] })
  } finally {
    translatingLocale.value = undefined
  }
}
const onSubmit = async () => {
  console.log("123")

  if (isBusy.value) return

  clearServerErrors()
  isFetching.value = true
  try {
    await validateForm()

    if (!isValidForm.value) {
      message.error(`${i18n.t("offers.offer_form_validate_error")}`)
      return
    }

    const value = await findOrCreateAttributeValue({
      attributeCode: props.attributeCode,
      attributeValueType: props.attributeValueType,
      formValues: form
    })

    console.log("value", value)

    emit("submit", value)
    onClose()
  } catch (error) {
    console.log("error", error)
    const valueFields =
      props.attributeValueType === "STRING" ? ["value_mlang.uk", "value_mlang.ru"] : ["value"]
    setServerErrors(error, { value: valueFields, value_mlang: valueFields })
  } finally {
    isFetching.value = false
  }
}

watch(
  () => props.visible,
  (value) => {
    if (!value) {
      resetFields()
      clearServerErrors()
      form.value_mlang.uk = ""
      form.value_mlang.ru = ""
      form.value = undefined
      form.unit = undefined
    }
  }
)
</script>

<style lang="scss" scoped>
.attribute-value-form {
  ::v-deep .ant-form-item-label {
    line-height: 22px;
  }

  .ant-form-item {
    margin-bottom: 10px;
    padding-bottom: 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__row {
    display: flex;
    gap: 16px;

    .ant-input {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
